/**
 * Route Deviation & Dynamic Recalculation Utilities
 * Detects when a driver deviates (takes a shortcut or alternative path) from the planned route.
 */

export const DEFAULT_DEVIATION_THRESHOLD_METERS = 50; // 50 meters off route = deviation
export const DEFAULT_RECALCULATION_COOLDOWN_MS = 10000; // 10 seconds cooldown between route API calls
export const ARRIVAL_THRESHOLD_METERS = 30; // 30 meters to destination = arrived, skip rerouting

/**
 * Calculate distance (in meters) between two coordinates using the Haversine formula.
 */
export function calculateDistanceMeters(lat1, lon1, lat2, lon2) {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return Infinity;
  const numLat1 = Number(lat1);
  const numLon1 = Number(lon1);
  const numLat2 = Number(lat2);
  const numLon2 = Number(lon2);
  if (!isFinite(numLat1) || !isFinite(numLon1) || !isFinite(numLat2) || !isFinite(numLon2)) return Infinity;

  const R = 6371000; // Earth's radius in meters
  const dLat = ((numLat2 - numLat1) * Math.PI) / 180;
  const dLon = ((numLon2 - numLon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((numLat1 * Math.PI) / 180) *
      Math.cos((numLat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate shortest perpendicular distance (in meters) from point P to line segment AB.
 */
export function distanceToSegmentMeters(pLat, pLng, aLat, aLng, bLat, bLng) {
  // Equirectangular projection centered at segment midpoint for meter conversion
  const cosLat = Math.cos((((aLat + bLat) / 2) * Math.PI) / 180);
  const degToMetersLat = 111320;
  const degToMetersLng = 111320 * cosLat;

  const dx = (bLng - aLng) * degToMetersLng;
  const dy = (bLat - aLat) * degToMetersLat;
  const px = (pLng - aLng) * degToMetersLng;
  const py = (pLat - aLat) * degToMetersLat;

  const segLengthSq = dx * dx + dy * dy;

  if (segLengthSq === 0) {
    return Math.sqrt(px * px + py * py);
  }

  // Projection scalar t clamped to [0, 1]
  const t = Math.max(0, Math.min(1, (px * dx + py * dy) / segLengthSq));
  const projX = t * dx;
  const projY = t * dy;

  const distX = px - projX;
  const distY = py - projY;
  return Math.sqrt(distX * distX + distY * distY);
}

/**
 * Calculate the minimum distance (in meters) from a point to a polyline.
 * @param {{ lat: number, lng: number } | { latitude: number, longitude: number }} point
 * @param {Array<{ lat: number, lng: number } | { latitude: number, longitude: number }>} polyline
 * @returns {number} Minimum distance in meters
 */
export function getMinDistanceToPolyline(point, polyline) {
  if (!point || !Array.isArray(polyline) || polyline.length === 0) {
    return Infinity;
  }

  const pLat = Number(point.lat ?? point.latitude);
  const pLng = Number(point.lng ?? point.longitude ?? point.lon);
  if (!isFinite(pLat) || !isFinite(pLng) || isNaN(pLat) || isNaN(pLng)) {
    return Infinity;
  }

  if (polyline.length === 1) {
    const pt0 = polyline[0];
    return calculateDistanceMeters(
      pLat,
      pLng,
      pt0.lat ?? pt0.latitude,
      pt0.lng ?? pt0.longitude ?? pt0.lon
    );
  }

  let minDistance = Infinity;

  for (let i = 0; i < polyline.length - 1; i++) {
    const p1 = polyline[i];
    const p2 = polyline[i + 1];
    const aLat = Number(p1.lat ?? p1.latitude);
    const aLng = Number(p1.lng ?? p1.longitude ?? p1.lon);
    const bLat = Number(p2.lat ?? p2.latitude);
    const bLng = Number(p2.lng ?? p2.longitude ?? p2.lon);

    if (!isFinite(aLat) || !isFinite(aLng) || !isFinite(bLat) || !isFinite(bLng)) {
      continue;
    }

    const dist = distanceToSegmentMeters(pLat, pLng, aLat, aLng, bLat, bLng);
    if (dist < minDistance) {
      minDistance = dist;
    }
  }

  return minDistance;
}

/**
 * Checks whether the driver has deviated from the given route.
 * @param {{ lat: number, lng: number } | { latitude: number, longitude: number }} driverLocation
 * @param {Array<{ lat: number, lng: number }>} routePath
 * @param {number} thresholdMeters
 * @returns {boolean}
 */
export function isDriverDeviated(driverLocation, routePath, thresholdMeters = DEFAULT_DEVIATION_THRESHOLD_METERS) {
  if (!driverLocation || !Array.isArray(routePath) || routePath.length < 2) {
    return false;
  }

  // Ignore GPS updates with very poor accuracy (> 100 meters) to avoid false reroutes
  if (driverLocation.accuracy && driverLocation.accuracy > 100) {
    return false;
  }

  const minDistance = getMinDistanceToPolyline(driverLocation, routePath);
  return minDistance > thresholdMeters;
}

/**
 * Resolves the target destination ({ lat, lng }) for an active trip.
 * @param {string} statusOrStage
 * @param {object} pickupLocation
 * @param {object} dropoffLocation
 * @returns {{ lat: number, lng: number } | null}
 */
export function resolveTargetDestination(statusOrStage, pickupLocation, dropoffLocation) {
  const norm = String(statusOrStage || '').trim().toUpperCase();

  const toLatLng = (loc) => {
    if (!loc) return null;
    const lat = Number(loc.latitude ?? loc.lat);
    const lng = Number(loc.longitude ?? loc.lng ?? loc.lon);
    if (!isFinite(lat) || !isFinite(lng) || isNaN(lat) || isNaN(lng)) return null;
    return { lat, lng };
  };

  const pickup = toLatLng(pickupLocation);
  const dropoff = toLatLng(dropoffLocation);

  const isTowardsPickup = [
    'ASSIGNED',
    'CONFIRMED',
    'SCHEDULED',
    'TOWARDS PICKUP',
    'TOWARDS_PICKUP',
    'EN_ROUTE_TO_PICKUP',
  ].includes(norm) || (norm.includes('PICKUP') && !norm.includes('AT_PICKUP') && !norm.includes('AT PICKUP'));

  const isTowardsDropoff = [
    'IN_PROGRESS',
    'INPROGRESS',
    'STARTED',
    'TOWARDS DROP',
    'TOWARDS_DROP',
    'ARRIVED',
    'EN_ROUTE_TO_DROPOFF',
    'AT_PICKUP',
  ].includes(norm) || norm.includes('DROP');

  if (isTowardsPickup && pickup) {
    return pickup;
  }
  if (isTowardsDropoff && dropoff) {
    return dropoff;
  }

  // Fallback: If dropoff exists, that is destination; otherwise pickup
  return dropoff || pickup || null;
}
